// Cold point editor: name, place in the vehicle, sensor preset + number (or expert keys),
// safe range presets, alarm delay and no-data minutes (spec §9 Setup).
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { todayInTz } from '@/features/staff/attendanceModel';
import { SOURCE_FAMILIES, RANGE_PRESETS, emptyForm, fromPoint, applyRangePreset, validateForm, toPayload, reasonRequiredFor } from './coldPointFormModel';

const control = 'mt-1 block w-full h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
const labelCls = 'block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400';
const errorCls = 'mt-1 block text-xs text-rose-600';

export default function ColdPointForm({ initial = null, onSubmit, onCancel, busy = false, tz = 'UTC', serverError = null }) {
  const { t } = useTranslation();
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  const [form, setForm] = useState(() => (initial ? fromPoint(initial) : emptyForm()));
  const [errors, setErrors] = useState({});
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const initialPayload = useMemo(() => (initial ? toPayload(fromPoint(initial)) : null), [initial]);
  const reasonFields = initialPayload ? reasonRequiredFor(initialPayload, toPayload(form)) : [];
  // Touching the calibration record stamps "Calibrated on" with today (org timezone) when it is still empty.
  const setCalibration = (patch) => setForm((f) => ({ ...f, ...patch, calibratedOn: patch.calibratedOn ?? (f.calibratedOn || todayInTz(tz)) }));

  const sourceLabel = {
    eye: t('coldChain.source.eye', 'EYE Sensor'),
    ble: t('coldChain.source.ble', 'BLE temperature logger'),
    wired: t('coldChain.source.wired', 'Wired probe'),
    custom: t('coldChain.source.custom', 'Custom keys (expert)'),
  };
  const rangeLabel = {
    chilled: t('coldChain.range.chilled', '2–8 °C'),
    cool: t('coldChain.range.cool', '0–4 °C'),
    frozen: t('coldChain.range.frozen', '≤ −18 °C'),
    custom: t('coldChain.range.custom', 'Custom'),
  };

  const keyErrorText = (code) => ({
    required: t('coldChain.form.errors.keys', 'Enter at least one key'),
    invalid: t('coldChain.form.errors.keysInvalid', 'Use keys like io10800 or io10800*0.01 (up to 8)'),
    inverted: t('coldChain.form.errors.keysInverted', 'Only door keys can start with !'),
    number: t('coldChain.form.errors.scale', 'Enter a number other than 0'),
  }[code]);
  const fieldError = (field) => (errors[field]
    ? <span role="alert" className={errorCls}>{keyErrorText(errors[field])}</span>
    : null);

  function submit(e) {
    e.preventDefault();
    const found = validateForm(form, initialPayload);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(toPayload(form, initial));
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={id('name')} className={labelCls}>{t('coldChain.form.name', 'Name')}</label>
          <input id={id('name')} className={control} value={form.name} placeholder={t('coldChain.form.namePlaceholder', 'Rear compartment')}
            onChange={(e) => set({ name: e.target.value })} />
          {errors.name && <span role="alert" className={errorCls}>{errors.name === 'length' ? t('coldChain.form.errors.nameLength', 'At most 60 characters') : t('coldChain.form.errors.name', 'Enter a name')}</span>}
        </div>
        <div>
          <label htmlFor={id('detail')} className={labelCls}>{t('coldChain.form.locationDetail', 'Location detail')}</label>
          <input id={id('detail')} className={control} value={form.locationDetail} onChange={(e) => set({ locationDetail: e.target.value })} />
          {errors.locationDetail && <span role="alert" className={errorCls}>{t('coldChain.form.errors.detailLength', 'At most 120 characters')}</span>}
        </div>
        <div>
          <label htmlFor={id('family')} className={labelCls}>{t('coldChain.form.sensor', 'Sensor')}</label>
          <select id={id('family')} className={control} value={form.family} onChange={(e) => set({ family: e.target.value })}>
            {SOURCE_FAMILIES.map((f) => <option key={f} value={f}>{sourceLabel[f]}</option>)}
          </select>
        </div>
        {form.family !== 'custom' ? (
          <>
            <div>
              <label htmlFor={id('slot')} className={labelCls}>{t('coldChain.form.sensorNumber', 'Sensor number')}</label>
              <select id={id('slot')} className={control} value={form.sensorSlot} onChange={(e) => set({ sensorSlot: Number(e.target.value) })}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {form.family === 'eye' && (
              <div>
                <label htmlFor={id('magnet')} className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  <input id={id('magnet')} type="checkbox" checked={!!form.doorMagnet} onChange={(e) => set({ doorMagnet: e.target.checked })} />
                  {t('coldChain.form.doorMagnet', 'Door magnet installed')}
                </label>
                <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">{t('coldChain.form.doorMagnetHint', 'Tick only when the EYE Sensor has its door magnet fitted. Without one the door state is not tracked.')}</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <label htmlFor={id('keys')} className={labelCls}>{t('coldChain.form.customKeys', 'Attribute keys (comma separated)')}</label>
              <input id={id('keys')} dir="ltr" className={`${control} font-mono`} value={form.customTempKeys} placeholder="io10800*0.01, eyeTemp1"
                onChange={(e) => set({ customTempKeys: e.target.value })} />
              {fieldError('customTempKeys')}
            </div>
            <div>
              <label htmlFor={id('scale')} className={labelCls}>{t('coldChain.form.customScale', 'Scale')}</label>
              <input id={id('scale')} dir="ltr" inputMode="decimal" className={control} value={form.customScale} onChange={(e) => set({ customScale: e.target.value })} />
              {fieldError('customScale')}
            </div>
            <div>
              <label htmlFor={id('hkeys')} className={labelCls}>{t('coldChain.form.humidityKeys', 'Humidity keys (optional)')}</label>
              <input id={id('hkeys')} dir="ltr" className={`${control} font-mono`} value={form.customHumidityKeys} onChange={(e) => set({ customHumidityKeys: e.target.value })} />
              {fieldError('customHumidityKeys')}
            </div>
            <div>
              <label htmlFor={id('hscale')} className={labelCls}>{t('coldChain.form.humidityScale', 'Humidity scale')}</label>
              <input id={id('hscale')} dir="ltr" inputMode="decimal" className={control} value={form.customHumidityScale} onChange={(e) => set({ customHumidityScale: e.target.value })} />
              {fieldError('customHumidityScale')}
            </div>
            <div>
              <label htmlFor={id('dkeys')} className={labelCls}>{t('coldChain.form.doorKeys', 'Door keys (optional)')}</label>
              <input id={id('dkeys')} dir="ltr" className={`${control} font-mono`} value={form.customDoorKeys} placeholder="!io10808"
                onChange={(e) => set({ customDoorKeys: e.target.value })} />
              <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">{t('coldChain.form.doorKeysHint', 'Start a key with ! when a true value means the door is closed.')}</span>
              {fieldError('customDoorKeys')}
            </div>
            <div>
              <label htmlFor={id('bkeys')} className={labelCls}>{t('coldChain.form.batteryKeys', 'Battery keys (optional)')}</label>
              <input id={id('bkeys')} dir="ltr" className={`${control} font-mono`} value={form.customBatteryKeys} onChange={(e) => set({ customBatteryKeys: e.target.value })} />
              {fieldError('customBatteryKeys')}
            </div>
            <div>
              <label htmlFor={id('bscale')} className={labelCls}>{t('coldChain.form.batteryScale', 'Battery scale')}</label>
              <input id={id('bscale')} dir="ltr" inputMode="decimal" className={control} value={form.customBatteryScale} onChange={(e) => set({ customBatteryScale: e.target.value })} />
              {fieldError('customBatteryScale')}
            </div>
          </>
        )}
        <div>
          <label htmlFor={id('offset')} className={labelCls}>{t('coldChain.form.offset', 'Calibration offset (°C)')}</label>
          <input id={id('offset')} dir="ltr" inputMode="decimal" className={control} value={form.tempOffset} onChange={(e) => setCalibration({ tempOffset: e.target.value })} />
          {errors.tempOffset && <span role="alert" className={errorCls}>{t('coldChain.form.errors.number', 'Enter a number')}</span>}
        </div>
      </div>

      <fieldset>
        <legend className={labelCls}>{t('coldChain.form.calibration', 'Calibration')}</legend>
        <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor={id('calref')} className={labelCls}>{t('coldChain.form.calibrationReference', 'Calibrated against')}</label>
            <input id={id('calref')} className={control} value={form.calibrationReference} onChange={(e) => setCalibration({ calibrationReference: e.target.value })} />
            {errors.calibrationReference && <span role="alert" className={errorCls}>{t('coldChain.form.errors.referenceLength', 'At most 200 characters')}</span>}
          </div>
          <div>
            <label htmlFor={id('calon')} className={labelCls}>{t('coldChain.form.calibratedOn', 'Calibrated on')}</label>
            <input id={id('calon')} type="date" dir="ltr" className={control} value={form.calibratedOn} onChange={(e) => setCalibration({ calibratedOn: e.target.value })} />
            {errors.calibratedOn && <span role="alert" className={errorCls}>{t('coldChain.form.errors.date', 'Enter a valid date')}</span>}
          </div>
          <div>
            <label htmlFor={id('caldue')} className={labelCls}>{t('coldChain.form.calibrationDueOn', 'Next calibration due')}</label>
            <input id={id('caldue')} type="date" dir="ltr" className={control} value={form.calibrationDueOn} onChange={(e) => setCalibration({ calibrationDueOn: e.target.value })} />
            {errors.calibrationDueOn && <span role="alert" className={errorCls}>{t('coldChain.form.errors.date', 'Enter a valid date')}</span>}
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend className={labelCls}>{t('coldChain.form.range', 'Safe range')}</legend>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {RANGE_PRESETS.map((r) => (
            <button key={r.id} type="button" aria-pressed={form.rangePreset === r.id} onClick={() => setForm((f) => applyRangePreset(f, r.id))}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold ${form.rangePreset === r.id ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
              {rangeLabel[r.id]}
            </button>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={id('min')} className={labelCls}>{t('coldChain.form.min', 'Min °C')}</label>
            <input id={id('min')} dir="ltr" inputMode="decimal" className={control} value={form.rangeMin} onChange={(e) => set({ rangeMin: e.target.value, rangePreset: 'custom' })} />
          </div>
          <div>
            <label htmlFor={id('max')} className={labelCls}>{t('coldChain.form.max', 'Max °C')}</label>
            <input id={id('max')} dir="ltr" inputMode="decimal" className={control} value={form.rangeMax} onChange={(e) => set({ rangeMax: e.target.value, rangePreset: 'custom' })} />
          </div>
        </div>
        {errors.range && <span role="alert" className={errorCls}>{t('coldChain.form.errors.range', 'Enter a min, a max, or both (min below max)')}</span>}
      </fieldset>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={id('delay')} className={labelCls}>{t('coldChain.form.alarmDelay', 'Alarm delay (min)')}</label>
          <input id={id('delay')} dir="ltr" inputMode="numeric" className={control} value={form.alarmDelayMinutes} onChange={(e) => set({ alarmDelayMinutes: e.target.value })} />
        </div>
        <div>
          <label htmlFor={id('nodata')} className={labelCls}>{t('coldChain.form.noData', 'No data after (min)')}</label>
          <input id={id('nodata')} dir="ltr" inputMode="numeric" className={control} value={form.noDataMinutes} onChange={(e) => set({ noDataMinutes: e.target.value })} />
        </div>
      </div>
      {(errors.alarmDelayMinutes || errors.noDataMinutes) && (
        <span role="alert" className={errorCls}>{t('coldChain.form.errors.minutes', 'Whole minutes only (no data: at least 2)')}</span>
      )}

      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
        <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="rounded border-slate-300" />
        {t('coldChain.form.enabled', 'Monitoring on')}
      </label>

      {initial && (
        <div>
          <label htmlFor={id('reason')} className={labelCls}>
            {t('coldChain.form.reason', 'Reason for change')}{reasonFields.length > 0 && ' *'}
          </label>
          <textarea id={id('reason')} rows={2} maxLength={500} aria-required={reasonFields.length > 0 ? 'true' : 'false'}
            className={`${control} h-auto py-2`} value={form.reason} onChange={(e) => set({ reason: e.target.value })} />
          {reasonFields.length > 0 && (
            <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">
              {t('coldChain.form.reasonHint', 'Needed because {{fields}} changed', { fields: reasonFields.join(', ') })}
            </span>
          )}
          {(errors.reason || serverError?.code === 'reason_required') && (
            <span role="alert" className={errorCls}>{t('coldChain.form.errors.reason', 'Say why this changed')}</span>
          )}
          {serverError?.code === 'version_conflict' && (
            <span role="alert" className={errorCls}>{t('coldChain.form.errors.versionConflict', 'Someone else changed this cold point — the latest settings are loaded')}</span>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            {t('coldChain.form.cancel', 'Cancel')}
          </button>
        )}
        <button type="submit" disabled={busy} className="rounded-xl bg-blue-500 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-600 disabled:opacity-60">
          {t('coldChain.form.save', 'Save')}
        </button>
      </div>
    </form>
  );
}
